import { IonIcon, IonItem, IonPopover } from "@ionic/react";
import { useState } from "react";
import { timeOutline } from "ionicons/icons";
import { ProjectImage } from "@/components/molecules/project/project-image";
import { Card } from "@/components/ui/card";
import { type ProjectEmployeeDayCard } from "@/lib/backend/api";
import { Routes } from "@/lib/router";
import { formatTimeRange } from "@/utils/format-datetime";

type ProjectCardUpcomingProps = {
  card: ProjectEmployeeDayCard;
};

export function ProjectCardUpcoming({ card }: ProjectCardUpcomingProps) {
  const [showAllTimes, setShowAllTimes] = useState(false);
  const [showFunctionsPopover, setShowFunctionsPopover] = useState(false);

  // Handle available functions
  const availableFunctions = card.availableFunctions || [];
  const firstFunction = availableFunctions[0] || "";
  const hasMultipleFunctions = availableFunctions.length > 1;
  const remainingCount = availableFunctions.length - 1;

  const city = card.city;

  // Format time slots — in projecttijd. Een dagkaart draagt geen eigen zone, dus valt dit terug op
  // Amsterdam; zonder zone las een blok van 15:00-23:00 op een telefoon in Toronto 09:00-17:00.
  const timeSlots = card.availableTimeSlots || [];
  const hasMultipleTimes = timeSlots.length > 1;
  const firstTimeSlot = timeSlots[0];

  const firstTimeDisplay = firstTimeSlot
    ? formatTimeRange(firstTimeSlot.startAt, firstTimeSlot.endAt)
    : "";

  const handleTimeClick = (e: React.MouseEvent) => {
    if (hasMultipleTimes) {
      e.preventDefault();
      e.stopPropagation();
      setShowAllTimes(!showAllTimes);
    }
  };

  const handleFunctionsClick = (e: React.MouseEvent) => {
    if (hasMultipleFunctions) {
      e.preventDefault();
      e.stopPropagation();
      setShowFunctionsPopover(true);
    }
  };

  return (
    <IonItem
      button
      detail={false}
      routerLink={Routes.ProjectOverview(
        { id: card.projectId },
        // Pass the card's date so the detail page opens on this date's segment.
        { search: { fromUrl: Routes.Projects(), day: card.day.split("T")[0] } },
      )}
      routerDirection="forward"
      className="ion-no-padding mb-[2px] w-full [--border-width:0] [--inner-padding-end:0] [--padding-end:0] [--padding-start:0]"
      lines="none"
    >
      <Card
        className="w-full overflow-hidden rounded-2xl border border-solid border-bordercolor-primary"
        style={{ boxShadow: "0px 1px 2px 0px rgba(55, 65, 81, 0.05)" }}
      >
        <div className="flex w-full items-start justify-between gap-4 p-4">
          {/* Left side - Content */}
          <div className="min-w-0 flex-1">
            <div className="flex flex-col gap-1">
              <h3 className="truncate text-ellipsis whitespace-nowrap text-md font-medium leading-[1.5] text-txt-primary">
                {card.projectName}
              </h3>
              <div className="flex items-center gap-[6px]">
                {firstFunction && (
                  <div className="flex items-center gap-1">
                    <span className="truncate text-ellipsis whitespace-nowrap text-sm font-medium leading-[1.4] text-txt-tertiary">
                      {firstFunction}
                    </span>
                    {hasMultipleFunctions && (
                      <>
                        <button
                          id={`functions-trigger-${card.projectId}`}
                          type="button"
                          onClick={handleFunctionsClick}
                          className="bg-bg-secondary cursor-pointer rounded border border-solid border-bordercolor-primary px-2 py-1 text-xs font-medium text-txt-tertiary active:opacity-70"
                        >
                          +{remainingCount}
                        </button>
                        <IonPopover
                          trigger={`functions-trigger-${card.projectId}`}
                          isOpen={showFunctionsPopover}
                          onDidDismiss={() => setShowFunctionsPopover(false)}
                          side="bottom"
                          alignment="start"
                        >
                          <div className="flex flex-wrap gap-2 p-3">
                            {availableFunctions.map((func, index) => (
                              <div
                                key={index}
                                className="bg-bg-secondary rounded border border-solid border-bordercolor-primary px-3 py-1.5 text-sm font-medium text-txt-primary"
                              >
                                {func}
                              </div>
                            ))}
                          </div>
                        </IonPopover>
                      </>
                    )}
                  </div>
                )}
                {city && firstFunction && (
                  <div className="flex items-center pb-0 pt-[2px]">
                    <div className="h-[3px] w-[3px] shrink-0 rounded-full bg-txt-tertiary" />
                  </div>
                )}
                {city && (
                  <span className="truncate text-ellipsis whitespace-nowrap text-sm font-medium leading-[1.4] text-txt-tertiary">
                    {city}
                  </span>
                )}
              </div>
              {/* Time display */}
              {firstTimeDisplay && (
                <div className="mt-1 flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={handleTimeClick}
                    className={`inline-flex items-center gap-2 self-start rounded-full border border-solid border-bordercolor-primary px-2 py-1 ${hasMultipleTimes ? "cursor-pointer active:opacity-70" : ""}`}
                  >
                    <IonIcon icon={timeOutline} className="text-lg text-txt-tertiary" />
                    <span className="text-sm font-semibold leading-[1.4] text-txt-tertiary">
                      {firstTimeDisplay}
                    </span>
                    {hasMultipleTimes && (
                      <span className="text-xs font-medium text-txt-tertiary">
                        {showAllTimes ? "▲" : "▼"}
                      </span>
                    )}
                  </button>
                  {/* Dropdown list for additional times */}
                  {showAllTimes && hasMultipleTimes && (
                    <div className="flex flex-col gap-2">
                      {timeSlots.slice(1).map((slot, index) => (
                        <div
                          key={index}
                          className="inline-flex items-center gap-2 self-start rounded-full border border-bordercolor-primary px-2 py-1"
                        >
                          <IonIcon icon={timeOutline} className="text-lg text-txt-tertiary" />
                          <span className="text-sm font-semibold leading-[1.4] text-txt-tertiary">
                            {formatTimeRange(slot.startAt, slot.endAt)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right side - Image */}
          <ProjectImage
            projectId={card.projectId}
            imageKey={card.projectImage}
            alt={card.projectName}
            className="aspect-video w-[120px] shrink-0 rounded-xl border border-bordercolor-primary"
          />
        </div>
      </Card>
    </IonItem>
  );
}
